import * as d3 from 'd3';
import { useRef, useEffect, useMemo } from 'react';
// Sub-components
import TripRibbon from './TripRibbon';

const interpolateRainbowTruncated = (x) => d3.interpolateRainbow(1 - (1-x)*0.8);

const TripRibbons = ({ num_trips, trips, radius, highlighted_trip, highlighted_person, selected_person_change, initial_anim_delays, set_focused_trip, set_selected_trip }) => {

  const colors = d3.quantize(interpolateRainbowTruncated, num_trips);

  // Piecewise linear function to determine opacity of unhighlighted ribbons
  // based on number of person trips displayed. Decreases from 0.2 to 0.05 as
  // number of person trips increases from 20 to 140.
  const unhighlighted_opacity = useMemo(() => {
    const displayed_trips = Object.values(trips).filter(t => t.in_filter);
    const num_person_trips = displayed_trips.reduce((acc, trip) => {
      const person_trip_count = trip.person_trips.filter((pt) => pt.person.in_filter).length;
      return acc + person_trip_count;
    }, 0);
    const clamped_input = Math.min(Math.max((num_person_trips-20)/120, 0), 1);
    const opacity = 0.05*clamped_input + 0.2*(1 - clamped_input);
    return opacity;
  }, [trips]);

  // True only for the very first commit. Read by TripRibbon's "on load" entrance
  // animation so it doesn't replay when a ribbon remounts later (e.g. reappearing
  // after being hidden by the voyager filter).
  const first_load = useRef(true);
  useEffect(() => {
    first_load.current = false;
  }, []);

  // Create ribbons
  let unhighlighted_ribbons = [];
  let highlighted_ribbons = [];
  for (let trip_index in trips) {
    trip_index = parseInt(trip_index);
    const trip = trips[trip_index];
    // Skip this trip if not in filter.
    if (!trip.in_filter) continue;
    // Number of people in this trip (post filter).
    const person_trip_count = trip.person_trips.filter((pt) => pt.person.in_filter).length;

    for (let i = 0; i < trip.person_trips.length; i++) {
      // Skip this person if not in filter.
      if (!trip.person_trips[i].person.in_filter) continue;
      // If trip has only 1 person (post filter), create a ribbon from
      // that person to themselves. Otherwise, create ribbons from each
      // person to every other person in the trip.
      const start_j = (person_trip_count === 1) ? i : i + 1;
      for (let j = start_j; j < trip.person_trips.length; j++) {
        // Skip this person if not in filter.
        if (!trip.person_trips[j].person.in_filter) continue;
        // Create new ribbon
        let source = trip.person_trips[i];
        let target = trip.person_trips[j];
        // Ensure that source and target are ordered according to person_index, not
        // the order in which they appear in trip.person_trips array.
        if (source.person_index > target.person_index) {
          [source, target] = [target, source];
        }

        const focused = (source.person_index === highlighted_person) ||
                        (target.person_index === highlighted_person) ||
                        (trip_index === highlighted_trip);
        const ribbons = focused ? highlighted_ribbons : unhighlighted_ribbons;
        ribbons.push(<TripRibbon radius = {radius}
                                 key={`${source.person_index}_${target.person_index}_${trip_index}`}
                                 source={source}
                                 target={target}
                                 fill={colors[trip_index]}
                                 focused={focused}
                                 unhighlighted_opacity={unhighlighted_opacity}
                                 trip_index={trip_index}
                                 selected_person_change={selected_person_change}
                                 initial_anim_delay={initial_anim_delays[trip_index]}
                                 first_load={first_load.current}
                                 set_focused_trip={set_focused_trip}
                                 set_selected_trip={set_selected_trip}/>);

      }
    }
  }
  const ribbons = unhighlighted_ribbons.concat(highlighted_ribbons);

  return (
    <g>
      {ribbons}
    </g>
  );
};

export default TripRibbons;
